import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft,
  Package,
  Truck,
  CheckCircle,
  X,
  RefreshCw,
  MoreHorizontal,
  Mail,
  Phone,
  MapPin,
  StickyNote,
  History,
  Printer,
} from "lucide-react";
import { OrderWorkflow } from "./OrderWorkflow";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import api from "@/utils/api";
import type { Order, UpdateOrderStatusRequest } from "@/types";

const orderStatuses = [
  {
    label: "Pending",
    value: "pending",
    color: "bg-yellow-100 text-yellow-800",
  },
  {
    label: "Confirmed",
    value: "confirmed",
    color: "bg-blue-100 text-blue-800",
  },
  {
    label: "Processing",
    value: "processing",
    color: "bg-purple-100 text-purple-800",
  },
  {
    label: "Shipped",
    value: "shipped",
    color: "bg-indigo-100 text-indigo-800",
  },
  {
    label: "Delivered",
    value: "delivered",
    color: "bg-green-100 text-green-800",
  },
  { label: "Cancelled", value: "cancelled", color: "bg-red-100 text-red-800" },
  { label: "Refunded", value: "refunded", color: "bg-gray-100 text-gray-800" },
];

export function OrderDetails() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { storeId } = useCurrentStore();

  const [newNote, setNewNote] = useState("");
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");

  // Fetch order details
  const {
    data: orderData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["order", orderId],
    queryFn: async () => {
      const { data } = await api.get(`/api/orders/${orderId}`, {
        headers: { "Store-ID": storeId },
      });
      return data;
    },
    enabled: !!orderId && !!storeId,
  });

  // Update order status mutation
  const statusMutation = useMutation({
    mutationFn: async (statusData: UpdateOrderStatusRequest) => {
      const { data } = await api.patch(
        `/api/orders/${orderId}/status`,
        statusData,
        {
          headers: { "Store-ID": storeId },
        }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });

  // Add note mutation
  const noteMutation = useMutation({
    mutationFn: async ({
      note,
      isInternal,
    }: {
      note: string;
      isInternal: boolean;
    }) => {
      const { data } = await api.post(
        `/api/orders/${orderId}/notes`,
        {
          note,
          isInternal,
        },
        {
          headers: { "Store-ID": storeId },
        }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      setNewNote("");
    },
  });

  // Update fulfillment mutation
  const fulfillmentMutation = useMutation({
    mutationFn: async ({
      type,
      metadata,
    }: {
      type: string;
      metadata?: any;
    }) => {
      const { data } = await api.patch(
        `/api/orders/${orderId}/fulfillment`,
        {
          type,
          status: true,
          metadata,
        },
        {
          headers: { "Store-ID": storeId },
        }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      setTrackingNumber("");
    },
  });

  // Cancel order mutation
  const cancelMutation = useMutation({
    mutationFn: async ({ reason }: { reason: string }) => {
      const { data } = await api.post(
        `/api/orders/${orderId}/cancel`,
        {
          reason,
        },
        {
          headers: { "Store-ID": storeId },
        }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });

  // Process refund mutation
  const refundMutation = useMutation({
    mutationFn: async ({
      amount,
      reason,
    }: {
      amount: number;
      reason: string;
    }) => {
      const { data } = await api.post(
        `/api/orders/${orderId}/refund`,
        {
          amount,
          reason,
        },
        {
          headers: { "Store-ID": storeId },
        }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      setRefundAmount("");
      setRefundReason("");
    },
  });

  // Update payment status mutation
  const paymentStatusMutation = useMutation({
    mutationFn: async ({
      status,
      transactionId,
    }: {
      status: string;
      transactionId?: string;
    }) => {
      const { data } = await api.patch(
        `/api/orders/${orderId}/payment`,
        {
          paymentStatus: status,
          transactionId,
        },
        {
          headers: { "Store-ID": storeId },
        }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });

  const order: Order = orderData?.order;

  const formatCurrency = (amount: number, currency = "NGN") => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    const statusConfig = orderStatuses.find((s) => s.value === status);
    return statusConfig?.color || "bg-gray-100 text-gray-800";
  };

  const handleStatusUpdate = (status: string, note?: string) => {
    statusMutation.mutate({
      status: status as Order["status"],
      note,
      notifyCustomer: true,
    });
  };

  const handleAddNote = () => {
    if (!newNote.trim()) return;
    noteMutation.mutate({
      note: newNote.trim(),
      isInternal: isInternalNote,
    });
  };

  const handleShipOrder = () => {
    if (!trackingNumber.trim()) return;
    fulfillmentMutation.mutate({
      type: "shipped",
      metadata: {
        trackingNumber: trackingNumber.trim(),
        carrier: "Default Carrier", // You could make this configurable
      },
    });
  };

  const printInvoice = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <RefreshCw className="h-6 w-6 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold">Order not found</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="text-xl font-bold">Order #{order.orderNumber}</h2>
            <p className="text-muted-foreground text-sm">
              Placed on{" "}
              {format(new Date(order.createdAt), "MMMM d, yyyy 'at' h:mm a")}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={printInvoice}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
             
              <Button variant="outline" size="sm">
                <MoreHorizontal className="h-4 w-4 mr-2" />
                More
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
               {/*
              <DropdownMenuItem>
                <Mail className="h-4 w-4 mr-2" />
                Send Invoice
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Download className="h-4 w-4 mr-2" />
                Export Order
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              */}
              <DropdownMenuItem
                className="text-red-600"
                onClick={() => {
                  const reason = prompt(
                    "Please provide a reason for cancellation:"
                  );
                  if (reason) {
                    cancelMutation.mutate({ reason });
                  }
                }}
                disabled={!["pending", "confirmed"].includes(order.status)}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel Order
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {/* Left Column - Order Details */}
        <div className="lg:col-span-2 space-y-6 overflow-hidden">
          {/* Order Workflow */}
          <OrderWorkflow order={order} onUpdate={refetch} />

          {/* Order Items */}
          <Card>
            <CardHeader>
              <CardTitle>Order Items</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 overflow-hidden">
              {order.items.map((item) => (
                <div
                  key={item._id}
                  className="flex items-center justify-between p-2 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {item.productSnapshot.images &&
                    item.productSnapshot.images.length > 0 ? (
                      (() => {
                        try {
                          // The image string is not valid JSON, so we need to extract the URL manually
                          const imageString = item.productSnapshot.images[0];
                          // Extract URL using regex - look for url: '...' pattern
                          const urlMatch = imageString.match(
                            /url:\s*['"`]([^'"`]+)['"`]/
                          );
                          if (urlMatch && urlMatch[1]) {
                            return (
                              <img
                                src={urlMatch[1]}
                                alt={item.productSnapshot.name}
                                className="w-16 h-16 object-cover rounded-lg border"
                              />
                            );
                          }
                          // Fallback: try to parse as JSON if it's actually valid JSON
                          const firstImage = JSON.parse(
                            item.productSnapshot.images[0]
                          );
                          return (
                            <img
                              src={firstImage.url}
                              alt={item.productSnapshot.name}
                              className="w-14 h-14 object-cover rounded-lg border"
                            />
                          );
                        } catch (error) {
                          console.error("Error parsing image:", error);
                          return (
                            <div className="w-16 h-16 bg-gray-100 rounded-lg border flex items-center justify-center">
                              <Package className="w-6 h-6 text-gray-400" />
                            </div>
                          );
                        }
                      })()
                    ) : (
                      <div className="w-14 h-14 bg-gray-100 rounded-lg border flex items-center justify-center">
                        <Package className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1 max-w-[200px]">
                      <div className="font-medium text-gray-900 text-sm truncate">
                        {item.productSnapshot.name}
                      </div>
                      {item.variantSnapshot && (
                        <div className="text-xs text-muted-foreground">
                          Variant: {item.variantSnapshot.name}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground">
                        Quantity: {item.quantity}
                      </div>
                      {item.options && Object.keys(item.options).length > 0 && (
                        <div className="text-xs text-muted-foreground">
                          {Object.entries(item.options).map(([key, value]) => (
                            <span key={key}>
                              {key}: {value}{" "}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-sm">
                      {formatCurrency(item.unitPrice)} each
                    </div>
                    <div className="text-muted-foreground text-xs">
                      Total: {formatCurrency(item.totalPrice)}
                    </div>
                  </div>
                </div>
              ))}

              <Separator />

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-medium">
                    {formatCurrency(order.pricing.subtotal)}
                  </span>
                </div>
                {order.pricing.discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span className="text-muted-foreground">
                      Discount{" "}
                      {order.discount?.code && `(${order.discount.code})`}
                    </span>
                    <span className="font-medium">
                      -{formatCurrency(order.pricing.discount)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="font-medium">
                    {formatCurrency(order.pricing.shipping)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span className="font-medium">
                    {formatCurrency(order.pricing.tax)}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between text-base font-semibold">
                  <span>Total</span>
                  <span>{formatCurrency(order.pricing.total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Order Timeline
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="space-y-4">
                {order.timeline.map((event, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm capitalize">
                          {event.status}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {format(
                            new Date(event.timestamp),
                            "MMM d, yyyy 'at' h:mm a"
                          )}
                        </span>
                      </div>
                      {event.note && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {event.note}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Customer & Actions */}
        <div className="space-y-6">
          {/* Customer Information */}
          <Card>
            <CardHeader>
              <CardTitle>Customer Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="font-medium text-sm">{order.customer.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {order.customer.email}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{order.customer.phone}</span>
              </div>
              {order.customer.address && (
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <span className="text-sm">{order.customer.address}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Information */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span>Method</span>
                <span className="text-sm capitalize">
                  {order.payment.method.replace("_", " ")}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Status</span>
                <Badge
                  className={`${
                    order.payment.status === "completed"
                      ? "bg-green-100 text-green-800"
                      : order.payment.status === "failed"
                      ? "bg-red-100 text-red-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {order.payment.status}
                </Badge>
              </div>
              {order.payment.transactionId && (
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Transaction ID
                  </Label>
                  <div className="font-mono text-sm">
                    {order.payment.transactionId}
                  </div>
                </div>
              )}
              {order.payment.proofOfPayment && (
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Proof of Payment
                  </Label>
                  <img
                    src={order.payment.proofOfPayment}
                    alt="Payment proof"
                    className="w-full max-w-xs rounded border"
                  />
                </div>
              )}

              {/* Payment Confirmation Actions */}
              {order.payment.status === "pending" && (
                <div className="pt-3 border-t">
                  <div className="space-y-3">
                    <div className="text-sm font-medium text-muted-foreground">
                      Payment Confirmation
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() =>
                          paymentStatusMutation.mutate({ status: "completed" })
                        }
                        disabled={paymentStatusMutation.isPending}
                        className="flex-1"
                      >
                        {paymentStatusMutation.isPending ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4 mr-2" />
                        )}
                        Confirm Payment
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() =>
                          paymentStatusMutation.mutate({ status: "failed" })
                        }
                        disabled={paymentStatusMutation.isPending}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Shipping Information */}
          <Card>
            <CardHeader>
              <CardTitle>Shipping Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <Truck className="h-4 w-4 text-muted-foreground" />
                <span className="capitalize">{order.shipping.method}</span>
              </div>
              {order.shipping.trackingNumber && (
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Tracking Number
                  </Label>
                  <div className="font-mono text-sm">
                    {order.shipping.trackingNumber}
                  </div>
                </div>
              )}
              {order.shipping.address && (
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Delivery Address
                  </Label>
                  <div className="text-sm">
                    <div>{order.shipping.address.line1}</div>
                    {order.shipping.address.line2 && (
                      <div>{order.shipping.address.line2}</div>
                    )}
                    <div>
                      {order.shipping.address.city},{" "}
                      {order.shipping.address.state}{" "}
                      {order.shipping.address.postalCode}
                    </div>
                    <div>{order.shipping.address.country}</div>
                  </div>
                </div>
              )}
              {order.shipping.deliveryInstructions && (
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Delivery Instructions
                  </Label>
                  <p className="text-sm">
                    {order.shipping.deliveryInstructions}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          

          {/* Order Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <StickyNote className="h-5 w-5" />
                Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Existing Notes */}
              {order.notes.customer && (
                <div className="p-3 bg-blue-50 rounded">
                  <Label className="text-sm font-medium text-blue-700">
                    Customer Note
                  </Label>
                  <p className="text-sm mt-1">{order.notes.customer}</p>
                </div>
              )}
              {order.notes.internal && (
                <div className="p-3 bg-yellow-50 rounded">
                  <Label className="text-sm font-medium text-yellow-700">
                    Internal Note
                  </Label>
                  <p className="text-sm mt-1">{order.notes.internal}</p>
                </div>
              )}

              {/* Add New Note */}
              <div className="space-y-2">
                <Label>Add Note</Label>
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note about this order..."
                  rows={3}
                />
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={isInternalNote}
                      onChange={(e) => setIsInternalNote(e.target.checked)}
                    />
                    Internal note (not visible to customer)
                  </label>
                  <Button
                    size="sm"
                    onClick={handleAddNote}
                    disabled={!newNote.trim() || noteMutation.isPending}
                  >
                    Add Note
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Refund Actions */}
          {order.payment.status === "completed" &&
            ["delivered", "shipped"].includes(order.status) && (
              <Card>
                <CardHeader>
                  <CardTitle>Refund</CardTitle>
                  <CardDescription>
                    Process a refund for this order
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Refund Amount</Label>
                    <Input
                      type="number"
                      max={order.pricing.total}
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      placeholder={`Max: ${formatCurrency(
                        order.pricing.total
                      )}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Reason</Label>
                    <Textarea
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      placeholder="Reason for refund..."
                      rows={3}
                    />
                  </div>
                  <Button
                    className="w-full"
                    variant="destructive"
                    onClick={() => {
                      const amount = parseFloat(refundAmount);
                      if (
                        amount > 0 &&
                        amount <= order.pricing.total &&
                        refundReason.trim()
                      ) {
                        refundMutation.mutate({
                          amount,
                          reason: refundReason.trim(),
                        });
                      }
                    }}
                    disabled={
                      !refundAmount ||
                      !refundReason.trim() ||
                      parseFloat(refundAmount) <= 0 ||
                      parseFloat(refundAmount) > order.pricing.total ||
                      refundMutation.isPending
                    }
                  >
                    Process Refund
                  </Button>
                </CardContent>
              </Card>
            )}
        </div>
      </div>
    </div>
  );
}
